import { useSeo, Reveal } from "../lib/motion";
import { Breadcrumb, CTASection } from "../components/CTASection";
import { Snowflake, Wrench, Thermometer, ClipboardCheck, Users, Check, ShieldCheck, MapPin } from "lucide-react";

const DOMAINES = [
  { icon: Snowflake, title: "Climatisation", text: "Mono-split, multi-split, gainable, DRV / VRV." },
  { icon: Wrench, title: "Entretien", text: "Maintenance, diagnostic et intervention." },
  { icon: Thermometer, title: "Froid commercial", text: "Chambres froides, groupes frigorifiques, équipements réfrigérés." },
  { icon: ClipboardCheck, title: "Mise en service & suivi", text: "Contrôle, réglages, mise en service et accompagnement." },
];

const APPROCHE = [
  { n: "01", t: "Étude", x: "Analyse du besoin, des volumes, de l’exposition, de l’isolation et de l’usage." },
  { n: "02", t: "Dimensionnement", x: "Choix d’une solution cohérente avec le bâtiment et l’utilisation." },
  { n: "03", t: "Installation", x: "Mise en œuvre propre et rigoureuse." },
  { n: "04", t: "Suivi", x: "Entretien, contrôle et dépannage des équipements." },
];

const POURQUOI = [
  "Entreprise locale depuis 2020",
  "Interlocuteur direct",
  "Expertise technique en climatisation et froid",
  "Suivi après installation",
];

function Section({ tint = "white", children, testid }) {
  return (
    <section data-testid={testid} className={tint === "cloud" ? "bg-cloud" : "bg-white"}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">{children}</div>
    </section>
  );
}

function Head({ icon: Icon, kicker, title, sub }) {
  return (
    <Reveal className="mb-10 max-w-3xl">
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ice">{Icon && <Icon size={15} />} {kicker}</span>
      <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink mt-4 leading-[1.05]">{title}</h2>
      {sub && <p className="text-maintext text-lg mt-4 leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

export default function Entreprise() {
  useSeo("HP Froid & Climatisation — Entreprise", "HP Froid & Climatisation, entreprise locale en activité depuis 2020 : climatisation, génie climatique et froid commercial à Strasbourg, Illkirch-Graffenstaden et dans le Bas-Rhin.");

  return (
    <div className="pt-32 bg-white">
      {/* 1. INTRODUCTION */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16" data-testid="entreprise-intro">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Entreprise" }]} />
        <Reveal className="mt-8 max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">L’entreprise</span>
          <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl text-ink mt-5 leading-[0.98]">HP Froid &amp; Climatisation</h1>
          <p className="font-heading font-semibold text-ice text-lg mt-4">Entreprise locale en activité depuis 2020</p>
          <p className="text-maintext text-lg mt-6 leading-relaxed">HP Froid &amp; Climatisation accompagne particuliers et professionnels dans leurs projets de climatisation, de génie climatique et de froid commercial.</p>
          <p className="text-maintext text-lg mt-4 leading-relaxed">Basée dans le secteur de Strasbourg / Illkirch-Graffenstaden, l’entreprise intervient principalement dans le Bas-Rhin pour l’installation, le remplacement, l’entretien et le dépannage des équipements.</p>
        </Reveal>
      </section>

      {/* 2. L'ÉQUIPE */}
      <Section tint="cloud" testid="entreprise-equipe">
        <Head icon={Users} kicker="L’équipe" title="Une entreprise à taille humaine" />
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
          {[{ name: "Pierre Leypold", role: "Président" }, { name: "Hakim Chahboun", role: "Directeur Général" }].map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div className="bg-white border border-line rounded-md p-7 h-full" data-testid={`equipe-${p.role === "Président" ? "president" : "dg"}`}>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-ice/10 text-ice font-heading font-black text-lg">{p.name.split(" ").map((n) => n[0]).join("")}</span>
                <p className="font-heading font-bold text-ink text-xl mt-5">{p.name}</p>
                <p className="text-ice font-semibold text-sm uppercase tracking-widest mt-1.5">{p.role}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8 max-w-3xl">
          <p className="text-maintext text-lg leading-relaxed">Les projets sont suivis directement par les dirigeants, de l’étude jusqu’à la mise en service et au suivi de l’installation.</p>
        </Reveal>
      </Section>

      {/* 3. DOMAINES D'INTERVENTION */}
      <Section testid="entreprise-domaines">
        <Head icon={Snowflake} kicker="Notre activité" title="Nos domaines d’intervention" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {DOMAINES.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.07}>
              <div className="bg-white border border-line rounded-md p-6 h-full hover:border-ice/40 hover:shadow-lg transition-[box-shadow,border-color] duration-300">
                <d.icon className="text-ice" size={24} />
                <h3 className="font-heading font-bold text-lg text-ink mt-4">{d.title}</h3>
                <p className="text-maintext text-[15px] mt-2 leading-relaxed">{d.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 4. NOTRE FAÇON DE TRAVAILLER */}
      <Section tint="cloud" testid="entreprise-approche">
        <Head icon={ClipboardCheck} kicker="Méthode" title="Une approche technique et pragmatique" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {APPROCHE.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.07}>
              <div className="border-t-2 border-ice pt-5 h-full">
                <span className="font-heading font-black text-3xl text-ice/25 leading-none">{s.n}</span>
                <p className="font-heading font-bold text-ink text-lg mt-3">{s.t}</p>
                <p className="text-maintext text-[15px] mt-2 leading-relaxed">{s.x}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 5. POURQUOI HP FROID + 6. QUALIFICATIONS */}
      <Section testid="entreprise-pourquoi">
        <Head icon={Check} kicker="Engagements" title="Pourquoi HP Froid" />
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl">
          {POURQUOI.map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <p className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed"><Check size={17} className="text-ice mt-0.5 shrink-0" /> {p}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 max-w-3xl">
          <div className="border-l-4 border-ice bg-cloud rounded-r-md p-5" data-testid="entreprise-qualifications">
            <p className="flex items-center gap-2 font-heading font-bold text-ink text-sm uppercase tracking-widest"><ShieldCheck size={16} className="text-ice" /> Sérieux technique</p>
            <p className="text-maintext text-[15px] mt-2.5 leading-relaxed">HP Froid intervient sur les équipements contenant des fluides frigorigènes dans le cadre de son attestation de capacité.</p>
          </div>
        </Reveal>
      </Section>

      {/* 7. MARQUES */}
      <Section tint="cloud" testid="entreprise-marques">
        <Head kicker="Équipements" title="Marques sur lesquelles nous intervenons" />
        <Reveal className="max-w-4xl">
          <p className="text-maintext text-lg leading-relaxed">Nous intervenons notamment sur les équipements Daikin, Mitsubishi Electric, Gree, Toshiba, Panasonic, Hitachi, Heiwa, Tecumseh, Friga-Bohn, LU-VE, Copeland, Danfoss, Bitzer, Rivacold, Carel, Dixell et Eliwell.</p>
        </Reveal>
      </Section>

      {/* 8. ZONE D'INTERVENTION */}
      <Section testid="entreprise-zone">
        <Head icon={MapPin} kicker="Secteur" title="Zone d’intervention" />
        <Reveal className="max-w-3xl">
          <p className="text-maintext text-lg leading-relaxed">HP Froid intervient principalement à Strasbourg, Illkirch-Graffenstaden et dans le Bas-Rhin, selon la nature et la taille du projet.</p>
        </Reveal>
      </Section>

      {/* 9. CTA FINAL */}
      <CTASection />
    </div>
  );
}
